import React, {
  useState,
  SyntheticEvent,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { ViewMode, GanttProps, Task } from "../../types/public-types";
import { GridProps } from "../grid/grid";
import {
  ganttDateRange,
  seedDates,
  VIEW_MODE_DEFAULT_VISIBLE_COUNT,
  VIEW_MODE_MAX_VISIBLE_COUNT,
} from "../../helpers/date-helper";
import { CalendarProps } from "../calendar/calendar";
import { TaskGanttContentProps } from "./task-gantt-content";
import { TaskListHeaderDefault } from "../task-list/task-list-header";
import { TaskListTableDefault } from "../task-list/task-list-table";
import { StandardTooltipContent, Tooltip } from "../other/tooltip";
import { VerticalScroll } from "../other/vertical-scroll";
import { TaskListProps, TaskList } from "../task-list/task-list";
import { TaskGantt } from "./task-gantt";
import { BarTask } from "../../types/bar-task";
import { convertToBarTasks } from "../../helpers/bar-helper";
import { GanttEvent } from "../../types/gantt-task-actions";
import { DateSetup } from "../../types/date-setup";
import { HorizontalScroll } from "../other/horizontal-scroll";
import hScrollStyles from "../other/horizontal-scroll.module.css";
import { removeHiddenTasks, sortTasks } from "../../helpers/other-helper";
import styles from "./gantt.module.css";

// Minimum share of the wrapper width reserved for the Gantt chart. The task-list
// table is capped at the remaining width; past that the table scrolls horizontally
// instead of shrinking the chart below this floor.
const GANTT_MIN_WIDTH_RATIO = 0.4;
// Width (px) of the column-resize divider rendered between the table and chart.
const TABLE_GANTT_DIVIDER_WIDTH = 20;

export const Gantt: React.FunctionComponent<GanttProps> = ({
  tasks,
  primaryPath,
  secondaryPath,
  leafTasks = [],
  scheduleType = "main",
  startDate = new Date(),
  endDate = new Date(),
  headerHeight = 50,
  columnWidth: columnWidthProp,
  listCellWidth = "155px",
  rowHeight = 50,
  ganttHeight = 0,
  viewMode = ViewMode.Day,
  preStepsCount = 1,
  locale = "en-US",
  barFill = 60,
  barCornerRadius = 3,
  barProgressColor = "#a3a3ff",
  barProgressSelectedColor = "#8282f5",
  barBackgroundColor = "#b8c2cc",
  barBackgroundSelectedColor = "#aeb8c2",
  projectProgressColor = "#7db59a",
  projectProgressSelectedColor = "#59a985",
  projectBackgroundColor = "#fac465",
  projectBackgroundSelectedColor = "#f7bb53",
  milestoneBackgroundColor = "#f1c453",
  milestoneBackgroundSelectedColor = "#f29e4c",
  rtl = false,
  handleWidth = 8,
  timeStep = 300000,
  arrowColor = "grey",
  fontFamily = "Arial, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue",
  fontSize = "14px",
  arrowIndent = 20,
  todayColor = "rgba(252, 248, 227, 0.5)",
  weekendColor = "#e6e4e4",
  viewDate,
  TooltipContent = StandardTooltipContent,
  TaskListHeader = TaskListHeaderDefault,
  TaskListTable = TaskListTableDefault,
  onDateChange,
  onProgressChange,
  onDoubleClick,
  onClick,
  onDelete,
  onSelect,
  onExpanderClick,
  onMultiSelect,
  taskLabelRenderer,
  delayToRender = 500,
  shouldNotShowLoadingOverlay=true,
  projectCalendar,
  onCalendarError,
  sliderTime,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const taskListRef = useRef<HTMLDivElement>(null);
  const isDraggingTable = useRef(false);
  // True once the user has dragged the resize bar — after that, columnWidth
  // is locked so pane resizing grows the visible count instead of fattening cols.
  const userResizedRef = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const dragMaxWidth = useRef<number>(Infinity);
  const [tableContainerWidth, setTableContainerWidth] = useState<number | null>(null);
  const tableInnerScrollRef = useRef<HTMLDivElement>(null);
  const tableHorizontalContainerRef = useRef<HTMLDivElement>(null);
  const tableHScrollRef = useRef<HTMLDivElement>(null);
  // True total width of the table's columns, reported up by TaskList. Drives both
  // the table width cap (no white space) and the horizontal scrollbar extent.
  const [tableContentWidth, setTableContentWidth] = useState(0);
  const [dateSetup, setDateSetup] = useState<DateSetup>(() => {
    const [startDateRange, endDateRange] = ganttDateRange(
      tasks,
      viewMode,
      preStepsCount
    );
    if (scheduleType === "lookAhead") {
      return { viewMode, dates: seedDates(startDate, endDate, viewMode) };
    }
    return {
      viewMode,
      dates: seedDates(startDateRange, endDateRange, viewMode),
    };
  });
  const [currentViewDate, setCurrentViewDate] = useState<Date | undefined>(
    undefined
  );

  const [taskListWidth, setTaskListWidth] = useState(0);
  const [wrapperWidth, setWrapperWidth] = useState(0);

  // Cap the table at the wrapper width minus the chart's reserved minimum share
  // (and the resize divider). Infinity until the wrapper has been measured.
  const maxTableWidth =
    wrapperWidth > 0
      ? Math.max(
          100,
          wrapperWidth * (1 - GANTT_MIN_WIDTH_RATIO) - TABLE_GANTT_DIVIDER_WIDTH
        )
      : Infinity;

  // The table may never be wider than its columns (no white space past the last
  // column) nor wider than maxTableWidth (chart keeps its 40% floor).
  const tableCap = Math.min(
    maxTableWidth,
    tableContentWidth > 0 ? tableContentWidth : Infinity
  );

  const handleTableResizeMouseDown = (e: React.MouseEvent) => {
    isDraggingTable.current = true;
    userResizedRef.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = tableContainerWidth ?? (taskListRef.current?.offsetWidth ?? 0);
    // Never let a drag exceed the columns' total width or shrink the chart below
    // its floor — both captured by tableCap.
    dragMaxWidth.current = tableCap;
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDraggingTable.current) return;
      const newWidth = Math.min(dragMaxWidth.current, Math.max(100, dragStartWidth.current + (e.clientX - dragStartX.current)));
      setTableContainerWidth(newWidth);
      setTaskListWidth(newWidth);
    };
    const onMouseUp = () => {
      isDraggingTable.current = false;
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);
  const [svgContainerWidth, setSvgContainerWidth] = useState(0);
  const [svgContainerHeight, setSvgContainerHeight] = useState(ganttHeight);
  const [barTasks, setBarTasks] = useState<BarTask[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [ganttEvent, setGanttEvent] = useState<GanttEvent>({
    action: "",
  });

  // Directly update a single bar task — called from TaskGanttContent on drag end
  // to sync barTasks to the final drag position before ganttEvent is cleared
  const handleBarTasksUpdate = (task: BarTask) => {
    setBarTasks(prev => prev.map(t => (t.id === task.id ? task : t)));
  };
  const taskHeight = useMemo(
    () => (rowHeight * barFill) / 100,
    [rowHeight, barFill]
  );

  const [selectedTask, setSelectedTask] = useState<BarTask>();
  const [failedTask, setFailedTask] = useState<BarTask | null>(null);

  // Auto-size columns per view-mode so the spec'd default count fits the
  // current chart pane. Keep re-snapping against svgContainerWidth while the
  // initial layout settles; once the user drags the resize bar (or the view
  // mode is settled), stop re-snapping so pane resizing grows the visible
  // count instead of fattening the columns.
  const [computedColumnWidth, setComputedColumnWidth] = useState<number | null>(null);
  const computedForViewModeRef = useRef<ViewMode | null>(null);
  useEffect(() => {
    if (columnWidthProp != null) return;
    if (svgContainerWidth <= 0) return;
    const minVisible = VIEW_MODE_DEFAULT_VISIBLE_COUNT[viewMode];
    if (!minVisible) return;
    const maxVisible = VIEW_MODE_MAX_VISIBLE_COUNT[viewMode];

    // View switch: carry the *relative* zoom across the switch instead of
    // snapping to whichever edge the carried-over width violates. Measure where
    // the previous view's visible count sat within its own [default..max] band
    // (t in [0,1]) and remap that same fraction onto the new view's band, then
    // convert the target visible count back to a column width. e.g. a Month view
    // sitting halfway between its default and max maps to a Day view sitting
    // halfway between its default and max. Mark it user-resized so the settling
    // branch below doesn't re-snap it back to the default count on the next render.
    const prevViewMode = computedForViewModeRef.current;
    if (
      prevViewMode != null &&
      prevViewMode !== viewMode &&
      computedColumnWidth != null
    ) {
      const visible = svgContainerWidth / computedColumnWidth;
      const oldMin = VIEW_MODE_DEFAULT_VISIBLE_COUNT[prevViewMode];
      const oldMax = VIEW_MODE_MAX_VISIBLE_COUNT[prevViewMode];

      let next = computedColumnWidth;
      if (maxVisible && oldMin != null && oldMax != null && oldMax > oldMin) {
        // Fraction through the old band: 0 = at its default count, 1 = at its max.
        const t = Math.min(1, Math.max(0, (visible - oldMin) / (oldMax - oldMin)));
        const targetVisible = minVisible + t * (maxVisible - minVisible);
        next = Math.floor(svgContainerWidth / targetVisible);
      } else if (visible < minVisible) {
        // Fallback (old view has no band): snap to the violated edge.
        next = Math.floor(svgContainerWidth / minVisible);
      } else if (maxVisible && visible > maxVisible) {
        next = Math.ceil(svgContainerWidth / maxVisible);
      }
      next = Math.max(20, next);
      computedForViewModeRef.current = viewMode;
      userResizedRef.current = true;
      if (next !== computedColumnWidth) {
        setComputedColumnWidth(next);
      }
      return;
    }

    // First computation / same-view settling: auto-size columns so the default
    // count fits, until the user drags the resize bar.
    const alreadySnappedForView =
      computedForViewModeRef.current === viewMode && computedColumnWidth != null;
    if (userResizedRef.current && alreadySnappedForView) return;
    const next = Math.max(20, Math.floor(svgContainerWidth / minVisible));
    computedForViewModeRef.current = viewMode;
    if (next !== computedColumnWidth) {
      setComputedColumnWidth(next);
    }
  }, [viewMode, svgContainerWidth, columnWidthProp, computedColumnWidth]);
  const columnWidth = columnWidthProp ?? computedColumnWidth ?? 60;

  // effectiveColumnWidth has two modes:
  //   1. Consumer supplied columnWidth → keep legacy fill-viewport stretch so
  //      content never leaves a gap on the right edge.
  //   2. Library-computed columnWidth → enforce the per-mode max-visible cap
  //      by lifting column width when the pane is wider than (max * columnWidth),
  //      so visible count never exceeds VIEW_MODE_MAX_VISIBLE_COUNT.
  // Math.ceil keeps svgWidth >= svgContainerWidth so there is no pixel gap on the right edge
  const effectiveColumnWidth = useMemo(() => {
    if (svgContainerWidth <= 0 || dateSetup.dates.length <= 0) return columnWidth;
    if (columnWidthProp == null) {
      const maxVisible = VIEW_MODE_MAX_VISIBLE_COUNT[viewMode];
      if (maxVisible) {
        return Math.max(columnWidth, Math.ceil(svgContainerWidth / maxVisible));
      }
      return columnWidth;
    }
    return Math.max(columnWidth, Math.ceil(svgContainerWidth / dateSetup.dates.length));
  }, [columnWidth, columnWidthProp, viewMode, svgContainerWidth, dateSetup.dates.length]);

  const svgWidth =
    effectiveColumnWidth < 55
      ? (dateSetup.dates.length + 0.5) * effectiveColumnWidth
      : dateSetup.dates.length * effectiveColumnWidth;
  const ganttFullHeight = useMemo(() => barTasks.length * rowHeight, [barTasks.length, rowHeight]);

  const [scrollY, setScrollY] = useState(0);
  const [scrollX, setScrollX] = useState(-1);
  const [ignoreScrollEvent, setIgnoreScrollEvent] = useState(false);
  const [lastTouchX, setLastTouchX] = useState(0);
  const [lastTouchY, setLastTouchY] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const buffer = rowHeight * 10;
  const visibleStartY = scrollY - buffer;
  const visibleEndY = scrollY + ganttHeight + buffer;
  useEffect(() => {
    if (scheduleType === "lookAhead" && startDate && endDate) {
      setDateSetup({
        viewMode,
        dates: seedDates(startDate, endDate, viewMode),
      });
    }
  }, [startDate, endDate]);
  // task change events
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if(!shouldNotShowLoadingOverlay){  
    setIsProcessing(true);
    }
    debounceRef.current = setTimeout(() => {
      let filteredTasks: Task[];
      // if (onExpanderClick) {
      filteredTasks = removeHiddenTasks(tasks);
      // } else {
      //   filteredTasks = tasks;
      // }
      filteredTasks = filteredTasks.sort(sortTasks);
      const [startDateRange, endDateRange] = ganttDateRange(
        filteredTasks,
        viewMode,
        preStepsCount,
        projectCalendar?.quarter_start ?? 0
      );
      let newDates = seedDates(startDateRange, endDateRange, viewMode);
      if (scheduleType === "lookAhead") {
        newDates = seedDates(startDate, endDate, viewMode);
      }
      if (rtl) {
        newDates = newDates.reverse();
        if (scrollX === -1) {
          setScrollX(newDates.length * effectiveColumnWidth);
        }
      }
      if (scheduleType !== "lookAhead") {
        setDateSetup({
          dates: seedDates(startDateRange, endDateRange, viewMode),
          viewMode,
        });
      }

      // // This is now handled by backend
      // let [primaryPath, secondaryPath] = getCriticalPaths(leafTasks);

      // Color the paths.
      uncolorAll(tasks);
      if (scheduleType !== "lookAhead") {
        colorPath(secondaryPath, "#00ff00", tasks, "secondary");
        colorPath(primaryPath, "#ff0000", tasks, "primary");
      }
      setBarTasks(
        convertToBarTasks(
          filteredTasks,
          newDates,
          effectiveColumnWidth,
          rowHeight,
          taskHeight,
          barCornerRadius,
          handleWidth,
          rtl,
          barProgressColor,
          barProgressSelectedColor,
          barBackgroundColor,
          barBackgroundSelectedColor,
          projectProgressColor,
          projectProgressSelectedColor,
          projectBackgroundColor,
          projectBackgroundSelectedColor,
          milestoneBackgroundColor,
          milestoneBackgroundSelectedColor
        )
      );
      setIsProcessing(false);
    }, delayToRender);
  }, [
    tasks,
    viewMode,
    preStepsCount,
    rowHeight,
    barCornerRadius,
    taskHeight,
    handleWidth,
    barProgressColor,
    barProgressSelectedColor,
    barBackgroundColor,
    barBackgroundSelectedColor,
    projectProgressColor,
    projectProgressSelectedColor,
    projectBackgroundColor,
    projectBackgroundSelectedColor,
    milestoneBackgroundColor,
    milestoneBackgroundSelectedColor,
    rtl,
    shouldNotShowLoadingOverlay,
    effectiveColumnWidth,
    projectCalendar,
  ]);
  // clear the pending debounce timer on unmount
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current!);
    };
  }, []);

  useEffect(() => {
    if (
      viewMode === dateSetup.viewMode &&
      ((viewDate && !currentViewDate) ||
        (viewDate && currentViewDate?.valueOf() !== viewDate.valueOf()))
    ) {
      const dates = dateSetup.dates;
      const index = dates.findIndex(
        (d, i) =>
          viewDate.valueOf() >= d.valueOf() &&
          i + 1 !== dates.length &&
          viewDate.valueOf() < dates[i + 1].valueOf()
      );
      if (index === -1) {
        return;
      }
      setCurrentViewDate(viewDate);
      setScrollX(effectiveColumnWidth * index);
    }
  }, [
    viewDate,
    effectiveColumnWidth,
    dateSetup.dates,
    dateSetup.viewMode,
    viewMode,
    currentViewDate,
    setCurrentViewDate,
  ]);

  useEffect(() => {
    const { changedTask, action } = ganttEvent;
    if (changedTask) {
      if (action === "delete") {
        setGanttEvent({ action: "" });
        setBarTasks(barTasks.filter(t => t.id !== changedTask.id));
      }
      // "move" / "start" / "end" / "progress": barTasks is updated on drag end
      // via onBarTasksUpdate callback (called from TaskGanttContent handleMouseUp).
      // During drag, task-gantt-content renders directly from ganttEvent.changedTask
      // for instant real-time updates without the extra render cycle.
    }
  }, [ganttEvent, barTasks]);

  useEffect(() => {
    if (failedTask) {
      setBarTasks(barTasks.map(t => (t.id !== failedTask.id ? t : failedTask)));
      setFailedTask(null);
    }
  }, [failedTask, barTasks]);

  useEffect(() => {
    if (!listCellWidth) {
      setTaskListWidth(0);
    }
    if (taskListRef.current) {
      setTaskListWidth(taskListRef.current.offsetWidth);
    }
  }, [taskListRef, listCellWidth]);

  // Keep the chart offset in sync when the task list's rendered width changes —
  // e.g. after a per-column resize. The effects above only re-run on listCellWidth,
  // so without this a column resize would leave svgContainerWidth stale. (The
  // scrollbar extent comes from tableContentWidth, reported by TaskList.)
  useEffect(() => {
    if (!listCellWidth || !taskListRef.current) return;
    if (typeof ResizeObserver === "undefined") return;
    const measure = () => {
      if (taskListRef.current) {
        setTaskListWidth(taskListRef.current.offsetWidth);
      }
    };
    const observer = new ResizeObserver(measure);
    observer.observe(taskListRef.current);
    measure();
    return () => observer.disconnect();
  }, [listCellWidth, scheduleType]);

  useEffect(() => {
    if (wrapperRef.current) {
      // Subtract the 20px column-resize divider so the gantt SVG fills the remaining space exactly
      setSvgContainerWidth(wrapperRef.current.offsetWidth - taskListWidth - 20);
    }
  }, [wrapperRef, taskListWidth, wrapperWidth]);

  // Track the wrapper's own width so the table cap (maxTableWidth) follows window
  // / container resizes, not just task-list changes.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    if (typeof ResizeObserver === "undefined") {
      setWrapperWidth(node.offsetWidth);
      return;
    }
    const measure = () => setWrapperWidth(node.offsetWidth);
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    measure();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (ganttHeight) {
      setSvgContainerHeight(ganttHeight + headerHeight);
    } else {
      setSvgContainerHeight(tasks.length * rowHeight + headerHeight);
    }
  }, [ganttHeight, tasks, headerHeight, rowHeight]);

  // scroll events
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (event.shiftKey || event.deltaX) {
        const scrollMove = event.deltaX ? event.deltaX : event.deltaY;
        let newScrollX = scrollX + scrollMove;
        if (newScrollX < 0) {
          newScrollX = 0;
        } else if (newScrollX > svgWidth) {
          newScrollX = svgWidth;
        }
        setScrollX(newScrollX);
        event.preventDefault();
      } else if (ganttHeight) {
        let newScrollY = scrollY + event.deltaY;
        if (newScrollY < 0) {
          newScrollY = 0;
        } else if (newScrollY > ganttFullHeight - ganttHeight) {
          newScrollY = ganttFullHeight - ganttHeight;
        }
        if (newScrollY !== scrollY) {
          setScrollY(newScrollY);
          event.preventDefault();
        }
      }

      setIgnoreScrollEvent(true);
    };

    const handleLogTouch = (event: TouchEvent) => {
      if (event) {
        setLastTouchX(event.changedTouches[0].pageX);
        setLastTouchY(event.changedTouches[0].pageY);
      }
    };

    const handleTouch = (event: TouchEvent) => {
      if (event.changedTouches[0].pageX || event.changedTouches[0].pageY) {
        let deltaX = event.changedTouches[0].pageX - lastTouchX;
        let deltaY = event.changedTouches[0].pageY - lastTouchY;

        // if (lastTouchX > 0 && lastTouchY > 0) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          let newScrollX = scrollX - deltaX / 3;
          if (newScrollX > 0 && newScrollX < 1600) {
            setScrollX(newScrollX);
          } else if (newScrollX < 0) {
            setScrollX(0);
          } else {
            setScrollX(1600);
          }
        } else {
          let newScrollY = scrollY - deltaY;
          if (newScrollY > 0 && newScrollY < 1600) {
            setScrollY(newScrollY);
          } else if (newScrollY < 0) {
            setScrollY(0);
          } else {
            setScrollY(1600);
          }
        }
        // }

        setLastTouchX(event.changedTouches[0].pageX);
        setLastTouchY(event.changedTouches[0].pageY);
      }

      // if (event.changedTouches[0].pageX) {
      //   const scrollMove = event.changedTouches[0].pageX ? event.changedTouches[0].pageX : event.changedTouches[0].pageY;
      //   let newScrollX = scrollX + scrollMove;
      //   if (newScrollX < 0) {
      //     newScrollX = 0;
      //   } else if (newScrollX > svgWidth) {
      //     newScrollX = svgWidth;
      //   }
      //   setScrollX(newScrollX);
      //   event.preventDefault();
      // } else if (ganttHeight) {
      //   let newScrollY = scrollY + event.changedTouches[0].pageY;
      //   if (newScrollY < 0) {
      //     newScrollY = 0;
      //   } else if (newScrollY > ganttFullHeight - ganttHeight) {
      //     newScrollY = ganttFullHeight - ganttHeight;
      //   }
      //   if (newScrollY !== scrollY) {
      //     setScrollY(newScrollY);
      //     event.preventDefault();
      //   }
      // }

      setIgnoreScrollEvent(true);
    };

    // subscribe if scroll is necessary
    wrapperRef.current?.addEventListener("wheel", handleWheel, {
      passive: false,
    });
    wrapperRef.current?.addEventListener("touchmove", handleTouch, {
      passive: false,
    });
    wrapperRef.current?.addEventListener("touchstart", handleLogTouch, {
      passive: false,
    });
    wrapperRef.current?.addEventListener("touchend", handleLogTouch, {
      passive: false,
    });
    return () => {
      wrapperRef.current?.removeEventListener("wheel", handleWheel);
      wrapperRef.current?.removeEventListener("touchmove", handleTouch);
      wrapperRef.current?.removeEventListener("touchend", handleLogTouch);
      wrapperRef.current?.removeEventListener("touchend", handleLogTouch);
    };
  }, [
    wrapperRef,
    scrollY,
    scrollX,
    ganttHeight,
    svgWidth,
    rtl,
    ganttFullHeight,
  ]);

  const handleScrollY = (event: SyntheticEvent<HTMLDivElement>) => {
    if (scrollY !== event.currentTarget.scrollTop && !ignoreScrollEvent) {
      setScrollY(event.currentTarget.scrollTop);
      setIgnoreScrollEvent(true);
    } else {
      setIgnoreScrollEvent(false);
    }
  };

  const handleScrollX = (event: SyntheticEvent<HTMLDivElement>) => {
    if (scrollX !== event.currentTarget.scrollLeft && !ignoreScrollEvent) {
      setScrollX(event.currentTarget.scrollLeft);
      setIgnoreScrollEvent(true);
    } else {
      setIgnoreScrollEvent(false);
    }
  };


  /**
   * Handles arrow keys events and transform it to new scroll
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    let newScrollY = scrollY;
    let newScrollX = scrollX;
    let isX = true;
    switch (event.key) {
      case "Down": // IE/Edge specific value
      case "ArrowDown":
        newScrollY += rowHeight;
        isX = false;
        break;
      case "Up": // IE/Edge specific value
      case "ArrowUp":
        newScrollY -= rowHeight;
        isX = false;
        break;
      case "Left":
      case "ArrowLeft":
        newScrollX -= effectiveColumnWidth;
        break;
      case "Right": // IE/Edge specific value
      case "ArrowRight":
        newScrollX += effectiveColumnWidth;
        break;
    }
    if (isX) {
      if (newScrollX < 0) {
        newScrollX = 0;
      } else if (newScrollX > svgWidth) {
        newScrollX = svgWidth;
      }
      setScrollX(newScrollX);
    } else {
      if (newScrollY < 0) {
        newScrollY = 0;
      } else if (newScrollY > ganttFullHeight - ganttHeight) {
        newScrollY = ganttFullHeight - ganttHeight;
      }
      setScrollY(newScrollY);
    }
    setIgnoreScrollEvent(true);
  };

  /**
   * Task select event
   */
  const handleSelectedTask = (taskId: string) => {
    const newSelectedTask = barTasks.find(t => t.id === taskId);
    const oldSelectedTask = barTasks.find(
      t => !!selectedTask && t.id === selectedTask.id
    );
    if (onSelect) {
      if (oldSelectedTask) {
        onSelect(oldSelectedTask, false);
      }
      if (newSelectedTask) {
        onSelect(newSelectedTask, true);
      }
    }
    setSelectedTask(newSelectedTask);
  };
  const handleExpanderClick = (task: Task) => {
    if (onExpanderClick && task.hideChildren !== undefined) {
      onExpanderClick({ ...task, hideChildren: !task.hideChildren });
    }
  };
  const gridProps: GridProps = {
    columnWidth: effectiveColumnWidth,
    svgWidth,
    tasks: tasks,
    scheduleType: scheduleType,
    rowHeight,
    dates: dateSetup.dates,
    todayColor,
    weekendColor,
    rtl,
    visibleStartY,
    visibleEndY,
    projectCalendar,
    viewMode,
  };
  const calendarProps: CalendarProps = {
    dateSetup,
    locale,
    viewMode,
    headerHeight,
    columnWidth: effectiveColumnWidth,
    fontFamily,
    fontSize,
    rtl,
    projectCalendar,
    weekendColor,
  };
  const barProps: TaskGanttContentProps = {
    tasks: barTasks,
    dates: dateSetup.dates,
    ganttEvent,
    selectedTask,
    rowHeight,
    taskHeight,
    columnWidth: effectiveColumnWidth,
    arrowColor,
    timeStep,
    fontFamily,
    fontSize,
    arrowIndent,
    svgWidth,
    rtl,
    setGanttEvent,
    setFailedTask,
    setSelectedTask: handleSelectedTask,
    onBarTasksUpdate: handleBarTasksUpdate,
    onDateChange,
    onProgressChange,
    onDoubleClick,
    onClick,
    onDelete,
    onCalendarError,
    projectCalendar,
    visibleStartY,
    visibleEndY,
    sliderTime,
  };

  const tableTasks = useMemo(() => {
    return ganttEvent.changedTask
      ? barTasks.map(t => (t.id === ganttEvent.changedTask!.id ? ganttEvent.changedTask! : t))
      : barTasks;
  }, [barTasks, ganttEvent.changedTask]);

  const tableProps: TaskListProps = {
    rowHeight,
    rowWidth: listCellWidth,
    fontFamily,
    fontSize,
    tasks: tableTasks,
    leafTasks,
    scheduleType,
    locale,
    headerHeight,
    scrollY,
    ganttHeight,
    horizontalContainerClass: styles.horizontalContainer,
    selectedTask,
    taskListRef,
    setSelectedTask: handleSelectedTask,
    onExpanderClick: handleExpanderClick,
    onDoubleClick,
    TaskListHeader,
    TaskListTable,
    taskLabelRenderer,
    onMultiSelect,
    // Only pin a fixed width once the user has dragged the divider; otherwise let
    // the table size to its content under the cap. tableCap bounds both at the
    // columns' total width (no white space) and the 40% Gantt floor.
    containerWidth:
      userResizedRef.current && tableContainerWidth != null
        ? Math.min(tableContainerWidth, tableCap)
        : undefined,
    containerMaxWidth: tableCap === Infinity ? undefined : tableCap,
    onContentWidthChange: setTableContentWidth,
    innerScrollRef: tableInnerScrollRef,
    horizontalContainerRef: tableHorizontalContainerRef,
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        className={styles.wrapper}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={wrapperRef}
      >
        {listCellWidth && <TaskList {...tableProps} />}
        {listCellWidth && scheduleType === "main" && (
          <div
            style={{
              width: "20px",
              cursor: "col-resize",
              backgroundColor: "#e0e0e0",
              flexShrink: 0,
              zIndex: 1,
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              userSelect: "none",
              fontSize: "10px",
              color: "#666",
              letterSpacing: "-1px",
            }}
            onMouseDown={handleTableResizeMouseDown}
          >
            &#9668;|&#9658;
          </div>
        )}
        <TaskGantt
          gridProps={gridProps}
          calendarProps={calendarProps}
          barProps={barProps}
          ganttHeight={ganttHeight}
          scrollY={scrollY}
          scrollX={scrollX}
        />
        {ganttEvent.changedTask && (
          <Tooltip
            arrowIndent={arrowIndent}
            rowHeight={rowHeight}
            svgContainerHeight={svgContainerHeight}
            svgContainerWidth={svgContainerWidth}
            fontFamily={fontFamily}
            fontSize={fontSize}
            scrollX={scrollX}
            scrollY={scrollY}
            task={ganttEvent.changedTask}
            type={ganttEvent.type as string}
            headerHeight={headerHeight}
            taskListWidth={taskListWidth}
            TooltipContent={TooltipContent}
            rtl={rtl}
            svgWidth={svgWidth}
            isDragging={
              ganttEvent.action === "move" ||
              ganttEvent.action === "start" ||
              ganttEvent.action === "end" ||
              ganttEvent.action === "progress"
            }
          />
        )}
        <VerticalScroll
          ganttFullHeight={ganttFullHeight}
          ganttHeight={ganttHeight}
          headerHeight={headerHeight}
          scroll={scrollY}
          onScroll={handleScrollY}
          rtl={rtl}
        />
      </div>
      {isProcessing && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "20px",
            zIndex: 9999,
            pointerEvents: "none", // ← allows scrolling & interaction underneath!
            borderRadius: "8px",
          }}
        >
          <span
            style={{
              fontSize: "24px",
              fontWeight: "600",
              color: "#cc0404",
              fontFamily: "Arial, sans-serif",
            }}
          >
            Loading...
          </span>
          <div className={styles.spinner} />
        </div>
      )}
      <div style={{ display: "flex" }}>
        {listCellWidth && (
          <div
            ref={tableHScrollRef}
            className={hScrollStyles.scrollWrapper}
            style={{ width: taskListWidth + 4, flexShrink: 0 }}
            onScroll={(e: SyntheticEvent<HTMLDivElement>) => {
              if (tableHorizontalContainerRef.current) {
                tableHorizontalContainerRef.current.scrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            <div style={{ width: tableContentWidth }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <HorizontalScroll
            svgWidth={svgWidth}
            taskListWidth={0}
            scroll={scrollX}
            rtl={rtl}
            onScroll={handleScrollX}
          />
        </div>
      </div>
    </div>
  );
};

// This is based on the visit function from https://en.wikipedia.org/wiki/Topological_sorting#Depth-first_search
export function topologicalOrderingHelper(
  taskID: string,
  taskMap: {},
  sortedTaskList: string[]
) {
  if (!taskMap[taskID]) return true; // dependency pointed to an ID not present in leafTasks. ignore the dependency
  if (taskMap[taskID].finished) return true;
  if (taskMap[taskID].started) {
    console.log("Cycle involving " + taskID);
    return false; // Error: dependencies made a cycle
  }

  taskMap[taskID].started = true;

  let task: Task = taskMap[taskID].task;
  if (task.dependencies)
    for (let i = 0; i < task.dependencies.length; i++) {
      let successVal = topologicalOrderingHelper(
        task.dependencies[i].id,
        taskMap,
        sortedTaskList
      );
      if (!successVal) return false; // Error: an error came from deeper recursive call
    }

  taskMap[taskID].finished = true;

  sortedTaskList.push(taskID);
  return true;
}
export function getCriticalPaths(leafTasks: Task[]) {
  // Exclude tasks that must not sit on a critical path:
  //  - Milestones: zero-duration markers, not work; they also frequently have an
  //    unset (date-0) start that injects a bogus multi-decade span.
  //  - Phantom tasks with no real schedule: a zero-length bar whose start sits at
  //    the epoch (date 0 = unset). As a predecessor of a long successor they
  //    inherit its full span with no overlap to cancel it, falsely dominating.
  // Bars still render from `tasks`; this only excludes them from path computation.
  leafTasks = leafTasks.filter(
    t =>
      t.type !== "milestone" &&
      (t.start.getTime() !== 0 || t.end.getTime() !== 0)
  );

  // Map ids to tasks for faster access.
  // O(n) space, O(n) time to add all from list, O(1) time for each access.
  let taskMap = {};
  for (let i = 0; i < leafTasks.length; i++)
    taskMap[leafTasks[i].id] = { task: leafTasks[i] };

  // Sort the tasks so each dependent is listed after its dependencies.
  // O(num vertices + num edges)
  let sortedTaskList: string[] = [];
  for (let i = 0; i < leafTasks.length; i++) {
    let successVal = topologicalOrderingHelper(
      leafTasks[i].id,
      taskMap,
      sortedTaskList
    );
    if (!successVal) return [[], []]; // Abort: there was a cycle
  }

  // Map tasks to their dependents for faster access.
  // Dependencies are already easy enough to access, but not dependents.
  // O(num vertices + num edges)
  for (let i = 0; i < leafTasks.length; i++)
    taskMap[leafTasks[i].id].dependents = [];
  for (let i = 0; i < leafTasks.length; i++) {
    let task = leafTasks[i];
    if (task.dependencies)
      for (let j = 0; j < task.dependencies.length; j++)
        taskMap[task.dependencies[j].id]?.dependents.push(task.id);
  }

  // Compute critical paths, in reverse order to handle dependents before dependencies.
  // O(num vertices + num edges)
  for (let i = sortedTaskList?.length - 1; i >= 0; i--)
    computeCriticalPath(sortedTaskList[i], taskMap);

  // Now each task has a list of all possible paths and their lengths
  // We want to combine and sort all these entries so we can trace any number
  // of critical paths by following the best available chain
  // O(num tasks)
  let taskChainList = [];
  for (let i = 0; i < sortedTaskList?.length; i++) {
    for (let j = 0; j < taskMap[sortedTaskList[i]].paths.length; j++) {
      taskChainList.push(taskMap[sortedTaskList[i]].paths[j]);
    }
  }

  // const printedList = taskChainList.map((elem) => {
  //   const newElem = { ...elem };
  //   newElem.duration = Math.round(elem.duration / 86400000);
  //   return newElem;
  // })

  // console.debug(printedList);

  if (taskChainList.length > 0) {

    // Sort by duration (descending)
    // O(n log n)
    taskChainList.sort((a, b) => b.duration - a.duration);

    // Select the best next option to build the best path
    let primaryLeaf = taskChainList[0].parent;
    let primaryPath = [];

    while (primaryLeaf !== undefined) {

      // Add this task to the primary path
      primaryPath.push(taskMap[primaryLeaf].task);

      // If this task has no dependents, it is a leaf and the primary path ends here
      if (taskMap[primaryLeaf].paths.length === 0) {
        primaryLeaf = undefined;
        break;
      }

      // Check all of this task's paths and select longest duration as next
      let primaryDuration = taskMap[primaryLeaf].paths[0].duration;
      let nextPath = taskMap[primaryLeaf].paths[0];
      for (let i = 1; i < taskMap[primaryLeaf].paths.length; i++) {
        let newDuration = taskMap[primaryLeaf].paths[i].duration;
        if (newDuration > primaryDuration) {
          nextPath = taskMap[primaryLeaf].paths[i]
          primaryDuration = newDuration;
        }
      }

      // Disqualify this path from being selected by second best path.
      nextPath.visited = true;

      // Follow this path down to the next task
      primaryLeaf = nextPath.task;

    }


    // Select the best next option from the unvisited tasks to build the second best path
    let secondaryLeaf = taskChainList[0].parent;

    for (let i = 0; i < taskChainList.length; i++) {
      if (!taskChainList[i].visited) {
        secondaryLeaf = taskChainList[i].parent;
        break;
      }
    }

    let secondaryPath = [];

    while (secondaryLeaf !== undefined) {

      // Add this task to the secondary path
      secondaryPath.push(taskMap[secondaryLeaf].task);

      // If this task has no dependents, it is a leaf and the secondary path ends here
      if (taskMap[secondaryLeaf].paths.length === 0) {
        secondaryLeaf = undefined;
        break;
      }

      // Check all of this task's paths and select longest duration as next
      let secondaryDuration = taskMap[secondaryLeaf].paths[0].duration;
      let nextPath = taskMap[secondaryLeaf].paths[0];

      // If default path has already been visited, discredit it
      if (nextPath.visited) secondaryDuration = 0;

      for (let i = 1; i < taskMap[secondaryLeaf].paths.length; i++) {
        let newDuration = taskMap[secondaryLeaf].paths[i].duration;
        // Extra check here to ensure path has not already been used
        if (newDuration > secondaryDuration && !taskMap[secondaryLeaf].paths[i].visited) {
          nextPath = taskMap[secondaryLeaf].paths[i]
          secondaryDuration = newDuration;
        }
      }

      // If path we ended up with has already been visited, this task is a leaf node
      if (nextPath.visited) {
        break;
      }

      // Disqualify this path from being selected by third best path.
      nextPath.visited = true;

      // Follow this path down to the next task
      secondaryLeaf = nextPath.task;

    }

    console.debug("Primary", primaryPath)
    console.debug("Secondary", secondaryPath)

    return [primaryPath, secondaryPath];
  }

  return [[], []];
}

export function computeCriticalPath(taskID: string, taskMap: {}) {
  let task: Task = taskMap[taskID].task;
  let dependents = taskMap[taskID].dependents;

  taskMap[taskID].start = task.start.getTime();
  taskMap[taskID].end = task.end.getTime();
  taskMap[taskID].paths = []; // Each task will track length of all dep paths
  for (let j = 0; j < dependents.length; j++) {

    let depStart = taskMap[dependents[j]].task.start.getTime();
    let depEnd = taskMap[dependents[j]].task.end.getTime();

    // Get the duration of the dep task
    let depDuration = depEnd - depStart;

    // Subtract off days and holidays from duration
    let startWeek = Math.floor(((depStart / 86400000) + 4) / 7);
    let endWeek = Math.floor(((depEnd / 86400000) + 4) / 7);
    let offTime = 0;

    if (taskMap[dependents[j]].task.calendar) {

      // Off Day Calculation
      if (taskMap[dependents[j]].task.calendar.off_days && taskMap[dependents[j]].task.calendar.off_days.length) {
        for (let d = 0; d < taskMap[dependents[j]].task.calendar.off_days.length || 0; d++) {
          for (let w = startWeek; w <= endWeek; w++) {
            let dayOfTheWeek = taskMap[dependents[j]].task.calendar.off_days[d];
            // Check each week to see if day is within duration
            let offDay = 86400000 * ((7 * w - 4) + dayOfTheWeek);
            // If so (and time isn't part of overlap with parent, which will be accounted for later), ...
            if (offDay >= depStart && offDay <= depEnd && (offDay > taskMap[taskID].end || taskMap[taskID].task.calendar.off_days.includes(dayOfTheWeek))) {
              // ...mark a day's worth of time to be subtracted from total duration
              offTime += 86400000;
            }
          }
        }
      }

      // Holiday Calculation
      if (taskMap[dependents[j]].task.calendar.holidays && taskMap[dependents[j]].task.calendar.holidays.length) {
        for (let h = 0; h < taskMap[dependents[j]].task.calendar.holidays.length || 0; h++) {
          // Check to see if holiday is within duration
          let holiday = taskMap[dependents[j]].task.calendar.holidays[h];
          // If so (and time isn't part of overlap with parent, which will be accounted for later), ...
          if (holiday >= depStart && holiday <= depEnd) {
            // ...mark a day's worth of time to be subtracted from total duration
            offTime += 86400000;
          }
        }
      }
    }

    // subtracting any overlap in days with its parent
    let overlap = Math.max((taskMap[taskID].end - depStart), 0);

    // Inherit all paths known by dep task, adding this task's length to the tracked lengths
    let paths = taskMap[dependents[j]].paths;
    if (paths && paths.length) {
      for (let k = 0; k < paths.length; k++) {

        let totalDuration = taskMap[taskID].end - taskMap[taskID].start - overlap - offTime + paths[k].duration

        if (taskMap[taskID].paths.length < 2) {
          taskMap[taskID].paths.push({
            duration: totalDuration,
            task: dependents[j],
            parent: taskID,
            visited: false
          });
        } else if (totalDuration > taskMap[taskID].paths[0].duration) {
          taskMap[taskID].paths[0].duration = totalDuration;
          taskMap[taskID].paths[0].task = dependents[j];
          taskMap[taskID].paths[0].parent = taskID;
          taskMap[taskID].paths[0].visited = false;
        } else if (totalDuration > taskMap[taskID].paths[1].duration) {
          taskMap[taskID].paths[1].duration = totalDuration;
          taskMap[taskID].paths[1].task = dependents[j];
          taskMap[taskID].paths[1].parent = taskID;
          taskMap[taskID].paths[1].visited = false;
        }

      }
    } else {
      // Dep has no paths of its own (it's a terminal leaf) → seed an immediate
      // child path ending at this dep.
      let immediateChildDuration = taskMap[taskID].end - taskMap[taskID].start + depDuration - overlap - offTime;

      // Create a new path that ends at the immediate dep task
      if (taskMap[taskID].paths.length < 2) {
        taskMap[taskID].paths.push({
          duration: immediateChildDuration,
          task: dependents[j],
          parent: taskID,
          visited: false
        });
      } else if (immediateChildDuration > taskMap[taskID].paths[0].duration) {
        taskMap[taskID].paths[0].duration = immediateChildDuration;
        taskMap[taskID].paths[0].task = dependents[j];
        taskMap[taskID].paths[0].parent = taskID;
        taskMap[taskID].paths[0].visited = false;
      } else if (immediateChildDuration > taskMap[taskID].paths[1].duration) {
        taskMap[taskID].paths[1].duration = immediateChildDuration;
        taskMap[taskID].paths[1].task = dependents[j];
        taskMap[taskID].paths[1].parent = taskID;
        taskMap[taskID].paths[1].visited = false;
      }
    }
  }
}

export function uncolorAll(tasks: Task[]) {
  tasks.forEach(task => {
    if (task.styles) delete task.styles.criticalPathColor;
    delete task.criticalPathArrows;
  });
}

export function colorPath(path: Task[], color: string, tasks: Task[],criticalPathType: string) {
  // Color boxes
  for (let i = 0; i < path.length; i++) {
    // For this task in the path, find the most immediate ancestor in tasks.
    let longestIDLength = 0;
    let longestTask;
    for (let j = 0; j < tasks.length; j++) {
      if (path[i].id === tasks[j].id) {
        longestTask = tasks[j];
        break;
      }
      if (
        path[i].id.startsWith(tasks[j].id + ".") &&
        tasks[j].id.length > longestIDLength
      ) {
        longestIDLength = tasks[j].id.length;
        longestTask = tasks[j];
      }
    }
    // If some ancestor is found, color it.
    if (longestTask) {
      let styles = longestTask.styles ?? {};
      styles.criticalPathColor = color;
      longestTask.styles = styles;
    }
  }
  // Color arrows
  for (let i = 0; i + 1 < path.length; i++) {
    // Items in the critical paths came from leafTasks, but we need to change the versions from tasks instead.
    let taskFromTasks;
    for (let j = 0; j < tasks.length; j++)
      if (path[i].id === tasks[j].id) {
        taskFromTasks = tasks[j];
        break;
      }
    if (taskFromTasks) {
      let arrows = taskFromTasks.criticalPathArrows;
      // Create the arrows list if it does not exist.
      if (!arrows) arrows = [];
      // If the arrow is already colored, replace its color in the list.
      let arrow = arrows.find(arrow => arrow.taskId === path[i + 1].id);
      if (arrow) {
        arrow.arrowColor = color;
        arrow.criticalPathType = criticalPathType;
      }
      // Else, add the new colored arrow to the list.
      else arrows.push({ taskId: path[i + 1].id, arrowColor: color, criticalPathType: criticalPathType });
      taskFromTasks.criticalPathArrows = arrows;
    }
  }
}
export const getParentWbs = (wbs: string): string | undefined => {
  const segments = wbs.split('.');
  if (segments.length <= 1) return undefined; // Top-level task has no parent
  return segments.slice(0, -1).join('.'); // Remove last segment
};
